import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import ContactSection from '../../components/ContactSection';
import { ArrowLeft, Calendar, Clock, Building2, Layers, RefreshCw, CheckCircle2 } from 'lucide-react';

const WhatIsDigitalTwinPage = () => {
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'What Is a Digital Twin? A Complete Guide for Construction and Facilities Management',
      description: 'Discover how digital twins are transforming construction and facilities management. Learn what they are, how they work, and why leading organisations are adopting them.',
      author: { '@type': 'Organization', name: 'Digital Scanning Group' },
      publisher: {
        '@type': 'Organization',
        name: 'Digital Scanning Group',
        logo: { '@type': 'ImageObject', url: 'https://www.digitalscanninggroup.com/logo.png' },
      },
      datePublished: '2026-04-28',
      dateModified: '2026-04-28',
      mainEntityOfPage: 'https://www.digitalscanninggroup.com/blog/what-is-a-digital-twin',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is a digital twin in construction?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A digital twin in construction is a detailed virtual replica of a physical building or site, created using 3D scanning technology such as Matterport or laser scanning. It provides an accurate, navigable digital model that can be used for design coordination, progress monitoring, and facilities management.',
          },
        },
        {
          '@type': 'Question',
          name: 'How much does a digital twin cost?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The cost of creating a digital twin varies depending on the size of the building, the level of detail required, and the technology used. A basic Matterport digital twin for a small commercial space may start from around £295, whilst larger or more complex projects requiring laser scanning and BIM integration can cost significantly more. Contact Digital Scanning Group for a tailored quotation.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the difference between a 3D model and a digital twin?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A 3D model is a static visual representation of a space, whereas a digital twin is a dynamic, data-rich replica that mirrors the physical asset in real time or near-real time. Digital twins can incorporate sensor data, maintenance records, and operational information, making them far more valuable for ongoing building management.',
          },
        },
        {
          '@type': 'Question',
          name: 'How does Matterport create digital twins?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Matterport cameras capture a space using a combination of structured light sensors, LiDAR, and high-resolution photography. The resulting data is processed in the cloud to create an immersive 3D virtual tour, accurate floor plans, and a point cloud that forms the foundation of a digital twin.',
          },
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.digitalscanninggroup.com' },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.digitalscanninggroup.com/blog' },
        { '@type': 'ListItem', position: 3, name: 'What Is a Digital Twin?', item: 'https://www.digitalscanninggroup.com/blog/what-is-a-digital-twin' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="What Is a Digital Twin? Complete Guide for Construction and FM"
        description="What are digital twins and how do they work in construction and facilities management? Discover their benefits. Read our guide."
        keywords="digital twin, digital twin construction, matterport digital twin, what is a digital twin, digital twin facilities management, digital twin building, 3D digital twin"
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/blog/what-is-a-digital-twin` : ''}
        ogType="article"
        structuredData={structuredData}
      />
      <Navbar scrolled={true} />

      <article className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Back Link */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-8">
            <ArrowLeft className="h-4 w-4" />
            Back to Blog
          </Link>

          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">Technology</span>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Calendar className="h-4 w-4" />
                28 April 2026
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Clock className="h-4 w-4" />
                8 min read
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Is a Digital Twin? A Complete Guide for Construction and Facilities Management
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              The term "digital twin" has become one of the most talked-about concepts in the built environment. But what exactly is a digital twin, how does it differ from a standard 3D model, and why are organisations across the UK investing in them? This guide explains everything you need to know.
            </p>
          </header>

          {/* Article Body */}
          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Understanding Digital Twins</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                A digital twin is a highly accurate virtual replica of a physical asset, space, or system. In the context of the built environment, it typically refers to a data-rich digital model of a building, infrastructure asset, or construction site that mirrors its real-world counterpart in precise detail.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Unlike a simple 3D model or architectural rendering, a digital twin is designed to be a living document. It can be updated over time to reflect changes in the physical asset, incorporate operational data from building management systems, and serve as a single source of truth for everyone involved in the asset's lifecycle.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                The concept originated in the aerospace and manufacturing sectors, where NASA and other organisations used digital replicas to monitor and maintain complex systems remotely. Today, the technology has matured significantly and is now widely accessible to the construction, healthcare, education, and facilities management sectors.
              </p>
              <p className="text-gray-600 leading-relaxed">
                At its core, a digital twin combines three elements: a spatial model (the geometry of the space), data layers (information about the assets within the space), and connectivity (the ability to update and interrogate the model over time). When these elements come together, the result is a powerful tool for decision-making, planning, and operational efficiency.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">How Digital Twins Work in Construction</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                In construction, digital twins serve multiple purposes across the project lifecycle. During the pre-construction phase, a digital twin of the existing site conditions -- captured through <Link to="/construction" className="text-blue-600 hover:underline">3D laser scanning</Link> or Matterport surveys -- provides the design team with accurate as-existing information. This reduces the risk of design clashes and costly rework once construction begins.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                During the construction phase, periodic reality capture creates time-stamped digital records that can be compared against the design model. This enables project managers to track progress remotely, verify that work has been completed to specification, and identify deviations before they become expensive problems. Each capture adds a new layer to the digital twin, building a comprehensive history of the project.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                At handover, the digital twin becomes an invaluable asset for the building owner or facilities management team. Rather than relying on paper-based O&M manuals and static floor plans, the FM team receives a navigable, measurable digital model of the building as it was actually built -- not as it was designed. This is particularly important given that construction projects frequently deviate from the original design.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Post-occupancy, the digital twin can be integrated with building management systems (BMS), IoT sensors, and maintenance databases. This transforms it from a static record into a dynamic operational tool that supports predictive maintenance, space utilisation analysis, and energy optimisation.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Benefits of Digital Twins</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {[
                  { icon: Building2, title: 'Reduced Site Visits', text: 'Stakeholders can review and measure spaces remotely, reducing travel costs and programme delays.' },
                  { icon: Layers, title: 'Single Source of Truth', text: 'All project stakeholders work from the same accurate, up-to-date model, eliminating version control issues.' },
                  { icon: RefreshCw, title: 'Lifecycle Value', text: 'The digital twin continues to deliver value long after construction is complete, supporting FM and refurbishment.' },
                  { icon: CheckCircle2, title: 'Better Decision-Making', text: 'Accurate spatial data combined with operational information enables evidence-based decisions.' },
                ].map((benefit, idx) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={idx} className="p-6 bg-blue-50 rounded-xl">
                      <div className="inline-flex p-3 bg-blue-100 rounded-lg mb-3">
                        <Icon className="h-6 w-6 text-blue-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                      <p className="text-gray-600">{benefit.text}</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Research from the Centre for Digital Built Britain (CDBB) has demonstrated that effective use of digital twins can reduce construction costs by up to 20% and operational costs by up to 30% over the building's lifetime. For large estates -- such as those managed by NHS trusts, local authorities, or corporate occupiers -- the cumulative savings can be substantial.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Beyond cost savings, digital twins improve safety outcomes. By enabling remote inspection of confined spaces, high-risk areas, or operational environments, they reduce the need for physical access and the associated health and safety risks. In <Link to="/healthcare" className="text-blue-600 hover:underline">healthcare settings</Link>, this is particularly valuable, as it minimises disruption to clinical activities.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Real-World Examples</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Digital twins are already in widespread use across the UK. Network Rail uses digital twins of its stations and infrastructure to plan maintenance and upgrades. The National Health Service is increasingly adopting digital twins for estate management, with several trusts commissioning <Link to="/blog/3d-scanning-nhs-hospitals" className="text-blue-600 hover:underline">3D scans of their hospital buildings</Link> to support backlog maintenance programmes and six-facet surveys.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                In the commercial property sector, landlords and managing agents use digital twins to provide prospective tenants with virtual tours, reducing void periods and marketing costs. The <Link to="/leisure" className="text-blue-600 hover:underline">leisure and hospitality sector</Link> uses them for venue marketing, event planning, and renovation coordination.
              </p>
              <p className="text-gray-600 leading-relaxed">
                On construction sites across London and the South East, digital twins created from regular Matterport and laser scan captures are replacing traditional photography-based progress reports. The result is a richer, more accurate record that can be interrogated long after the scaffolding has come down.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">How Digital Scanning Group Creates Digital Twins</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                At Digital Scanning Group, we create digital twins using a combination of Matterport Pro3 cameras, Leica and FARO laser scanners, and professional drone platforms. The choice of technology depends on the size, complexity, and intended use of the digital twin. You can learn more about our scanning capabilities on our <Link to="/matterport-virtual-tours-london" className="text-blue-600 hover:underline">Matterport virtual tours</Link> page.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                For most commercial and healthcare buildings, we use Matterport technology to create immersive, browser-accessible 3D virtual tours that double as operational digital twins. The Matterport platform provides measurement tools, tagging capabilities, and integrations with popular FM platforms such as Planon and Archibus.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                For projects requiring higher geometric accuracy -- such as <Link to="/construction" className="text-blue-600 hover:underline">scan-to-BIM workflows</Link> or complex refurbishment projects -- we deploy terrestrial laser scanners to capture point cloud data at sub-millimetre precision. This data can be registered, processed, and delivered as point clouds, Revit models, or AutoCAD drawings, depending on the client's requirements.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Every project begins with a thorough understanding of the client's objectives. Whether you need a simple virtual tour for marketing purposes or a comprehensive BIM model for a major refurbishment, our team will recommend the most appropriate technology and workflow to deliver the results you need, on time and within budget.
              </p>
            </section>

            {/* FAQ Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
              <div className="space-y-6">
                {[
                  { q: 'What is a digital twin in construction?', a: 'A digital twin in construction is a detailed virtual replica of a physical building or site, created using 3D scanning technology such as Matterport or laser scanning. It provides an accurate, navigable digital model that can be used for design coordination, progress monitoring, and facilities management.' },
                  { q: 'How much does a digital twin cost?', a: 'The cost varies depending on building size, level of detail, and technology used. A basic Matterport digital twin for a small commercial space may start from around £295, whilst larger projects requiring laser scanning and BIM integration cost significantly more. Contact us for a tailored quotation.' },
                  { q: 'What is the difference between a 3D model and a digital twin?', a: 'A 3D model is a static visual representation, whereas a digital twin is a dynamic, data-rich replica that can be updated over time. Digital twins incorporate operational information such as maintenance records and sensor data, making them far more valuable for ongoing building management.' },
                  { q: 'How does Matterport create digital twins?', a: 'Matterport cameras capture spaces using structured light sensors, LiDAR, and high-resolution photography. The data is processed in the cloud to create immersive 3D virtual tours, accurate floor plans, and point clouds that form the foundation of a digital twin.' },
                ].map((faq, idx) => (
                  <div key={idx} className="p-6 bg-gray-50 rounded-xl">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.q}</h3>
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Ready to Create a Digital Twin of Your Building?</h2>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Whether you need a Matterport virtual tour or a full laser scan with BIM deliverables, Digital Scanning Group can help. Get in touch for a no-obligation quotation.
              </p>
              <Link
                to="/construction"
                className="inline-flex px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                View Our Construction Services
              </Link>
            </section>
          </div>
        </div>
      </article>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default WhatIsDigitalTwinPage;
